import { useCallback, useEffect, useRef, useState, type FormEvent } from "react";

import {
  canManageObligations,
  createObligation,
  DAY_TYPE_LABELS,
  DEBTOR_LABELS,
  describeRule,
  fetchObligations,
  OBLIGATION_STATUS_META,
  canRegisterClosingEvent,
  OBLIGATION_DEFAULT_RESP_ROLE,
  OBLIGATION_TYPE_LABELS,
  RULE_BASE_LABELS,
  removeObligation,
  updateObligation,
  type Obligation,
  type ObligationDayType,
  type ObligationDebtor,
  type ObligationInput,
  type ObligationRuleBase,
  type ObligationRuleKind,
  type ObligationType,
} from "../lib/obligations";
import {
  availableKinds,
  EVENT_KIND_LABELS,
  fetchEvents,
  registerEvent,
  reverseEvent,
  type EventRegistered,
  type EventReversed,
  type ExecEvent,
  type ExecEventKind,
} from "../lib/events";
import { HelpLink } from "../help/links";
import { ROLE_LABELS, ROLES, type Role } from "../lib/roles";
import { ObligationLegalPanel } from "./ObligationLegalPanel";
import { canViewLegal } from "../lib/legal";
import type { Specification } from "../lib/contracts";
import { formatDate, formatMoney } from "../lib/format";

/**
 * Обязательства договора (E7): реестр с ВЫЧИСЛЕННЫМИ статусами (Р-2, Прил. Б), правилом срока и
 * связью-триггером; добавление/правка/удаление — только юрист/админ (`obligation:manage`, ТЗ 4.3;
 * авторитетная проверка на сервере, правило 5). Оформление — токены docs/07 (правило 9); статусы —
 * штампы с цветовыми токенами; даты/деньги — правило 8.
 *
 * События исполнения (E8): по каждому обязательству — остаток, лента событий и форма внесения.
 * Кнопка вида события показывается только тем ролям, которым он доступен (матрица 4.3), и только
 * тех видов, что подходят типу обязательства (Приложение А). Скрытая кнопка — удобство, а не
 * защита: отказ даёт сервер (правило 5, контрольный пример 15).
 *
 * Сторнирование (E8a): правку и удаление события заменяет запись сторно с причиной. Кнопка
 * «Сторнировать» показывается по признаку `canReverse` из ленты — его считает сервер по правилу
 * В19 (автор в день внесения, позже — юрист), клиент это правило не повторяет.
 */

const btnPrimary =
  "rounded-[var(--radius-m)] bg-accent px-3 py-1.5 text-[13px] font-bold text-surface disabled:opacity-50";
const btnSecondary =
  "rounded-[var(--radius-m)] border border-border px-3 py-1.5 text-[13px] font-bold text-ink hover:bg-bg disabled:opacity-50";
const inputCls =
  "w-full rounded-[var(--radius-m)] border border-border bg-surface px-2.5 py-1.5 text-[14px]";
const labelCls = "mb-1 block text-[13px] text-muted";

function StatusStamp({ o }: { o: Obligation }) {
  const meta = OBLIGATION_STATUS_META[o.status];
  return (
    <span className="inline-flex flex-wrap items-center gap-1">
      <span className={`stamp ${meta.cls}`}>{meta.label}</span>
      {o.escalated && (
        <span
          className="stamp text-overdue-fg bg-overdue-bg"
          title="Просрочка больше порога (ТЗ 3.1)"
        >
          Эскалация
        </span>
      )}
    </span>
  );
}

/** Срок и остаток дней (или «ожидает события»). */
function DueCell({ o }: { o: Obligation }) {
  if (o.awaiting) return <span className="text-[13px] text-muted">ожидает события</span>;
  if (!o.due) return <span className="text-muted">—</span>;
  const hint =
    o.status === "overdue"
      ? `просрочка ${o.overdue} дн.`
      : o.daysLeft != null && o.daysLeft >= 0
        ? `осталось ${o.daysLeft} дн.`
        : null;
  return (
    <span>
      <span className="num">{formatDate(o.due)}</span>
      {o.shifted && (
        <span className="ml-1 text-[11px] text-muted" title="Перенос по ст. 193 ГК РФ">
          ⤷
        </span>
      )}
      {hint && <span className="mt-0.5 block text-[11px] text-muted">{hint}</span>}
    </span>
  );
}

export function ObligationsSection({
  contractId,
  specifications,
  roles,
  highlightId = null,
}: {
  contractId: string;
  specifications: Specification[];
  roles: readonly Role[];
  /**
   * Обязательство, ради которого открыли карточку (переход из сводки, реестра или уведомления).
   * Строка подсвечивается и подкручивается в поле зрения: без этого пользователь, пришедший по
   * ссылке «просрочена оплата», попадал бы в общий список и искал нужную строку глазами.
   */
  highlightId?: string | null;
}) {
  const canManage = canManageObligations(roles);
  const [items, setItems] = useState<Obligation[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [adding, setAdding] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);

  const load = useCallback(async () => {
    const res = await fetchObligations(contractId);
    if (res.ok) {
      setItems(res.value);
      setError(null);
      return;
    }
    setError(res.message);
  }, [contractId]);

  useEffect(() => {
    void load();
  }, [load]);

  // Колонки: тип, должник, ответственный, сумма, остаток, срок, статус (+ действия у юриста/админа).
  const colCount = canManage ? 8 : 7;

  return (
    <section aria-labelledby="obl-title" className="mt-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h2 id="obl-title" className="text-[15px] font-bold text-ink">
          Обязательства
        </h2>
        {canManage && !adding && (
          <button type="button" onClick={() => setAdding(true)} className={btnPrimary}>
            Добавить
          </button>
        )}
      </div>

      {error && (
        <p role="alert" className="mt-2 text-[13px] text-overdue-fg">
          {error}
        </p>
      )}

      {canManage && adding && (
        <div className="mt-3">
          <ObligationForm
            contractId={contractId}
            specifications={specifications}
            obligations={items ?? []}
            onSaved={() => {
              setAdding(false);
              void load();
            }}
            onCancel={() => setAdding(false)}
          />
        </div>
      )}

      <div className="mt-3">
        {items === null ? (
          <p className="text-[13px] text-muted" role="status">
            Загрузка обязательств…
          </p>
        ) : items.length === 0 ? (
          <p className="text-[13px] text-muted">
            Обязательств пока нет.{" "}
            {canManage
              ? "Добавьте обязательство с правилом срока — статус вычислится автоматически."
              : "Их ведут юрист и администратор."}
          </p>
        ) : (
          <div className="min-w-fit rounded-[var(--radius-l)] border border-border bg-surface">
            <table className="sticky-head w-full border-collapse text-[14px]">
              <caption className="sr-only">Обязательства договора</caption>
              <thead>
                <tr className="border-b border-border text-left text-[12px] text-muted">
                  <th scope="col" className="px-4 py-2.5 font-bold">
                    Тип / описание
                  </th>
                  <th scope="col" className="px-4 py-2.5 font-bold">
                    Должник
                  </th>
                  <th scope="col" className="px-4 py-2.5 font-bold">
                    Ответственный
                  </th>
                  <th scope="col" className="px-4 py-2.5 font-bold">
                    Сумма
                  </th>
                  <th scope="col" className="px-4 py-2.5 font-bold">
                    Остаток
                  </th>
                  <th scope="col" className="px-4 py-2.5 font-bold">
                    Срок
                  </th>
                  <th scope="col" className="px-4 py-2.5 font-bold">
                    Статус
                  </th>
                  {canManage && (
                    <th scope="col" className="px-4 py-2.5 font-bold">
                      Действия
                    </th>
                  )}
                </tr>
              </thead>
              <tbody>
                {items.map((o) =>
                  editingId === o.id ? (
                    <tr key={o.id} className="border-b border-border-soft last:border-b-0">
                      <td colSpan={colCount} className="px-4 py-3">
                        <ObligationForm
                          contractId={contractId}
                          specifications={specifications}
                          obligations={items}
                          initial={o}
                          onSaved={() => {
                            setEditingId(null);
                            void load();
                          }}
                          onCancel={() => setEditingId(null)}
                        />
                      </td>
                    </tr>
                  ) : (
                    <ObligationRow
                      key={o.id}
                      o={o}
                      canManage={canManage}
                      roles={roles}
                      colCount={colCount}
                      highlighted={o.id === highlightId}
                      onEdit={() => setEditingId(o.id)}
                      onChanged={load}
                    />
                  ),
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </section>
  );
}

function ObligationRow({
  o,
  canManage,
  roles,
  colCount,
  highlighted = false,
  onEdit,
  onChanged,
}: {
  o: Obligation;
  canManage: boolean;
  roles: readonly Role[];
  colCount: number;
  /** Строка, ради которой открыли карточку: выделяется рамкой и подкручивается в поле зрения. */
  highlighted?: boolean;
  onEdit: () => void;
  onChanged: () => Promise<void>;
}) {
  const [confirm, setConfirm] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Раскрыта ли панель событий обязательства (лента + формы внесения).
  const [eventsOpen, setEventsOpen] = useState(false);
  const [legalOpen, setLegalOpen] = useState(false);
  const rowRef = useRef<HTMLTableRowElement>(null);
  const kinds = availableKinds(roles, o.type);

  // Подкрутить выделенную строку в поле зрения. В jsdom `scrollIntoView` не реализован (как и в
  // SettingsPage) — вызываем через проверку, иначе тесты падали бы на отсутствующем методе.
  useEffect(() => {
    if (!highlighted) return;
    rowRef.current?.scrollIntoView?.({ block: "center" });
  }, [highlighted]);

  const doDelete = async () => {
    setBusy(true);
    setError(null);
    const res = await removeObligation(o.id);
    setBusy(false);
    setConfirm(false);
    if (res.ok) {
      await onChanged();
      return;
    }
    setError(res.message);
  };

  return (
    <>
      <tr
        ref={rowRef}
        id={`obl-${o.id}`}
        // Выделение — рамкой акцентного токена (правило 9, без «сырых» hex). Держится, пока
        // пользователь не уйдёт с этого адреса: моргающая на пару секунд подсветка бесполезна
        // тому, кто открыл ссылку и отвлёкся.
        className={`border-b border-border-soft align-top last:border-b-0 ${
          highlighted ? "outline outline-2 -outline-offset-2 outline-accent" : ""
        }`}
        aria-current={highlighted ? "true" : undefined}
      >
        <td className="px-4 py-3">
          <div className="text-ink">{o.typeLabel}</div>
          <div className="text-[12px] text-muted">{o.descr}</div>
          <div className="mt-0.5 text-[11px] text-muted">срок: {describeRule(o)}</div>
          <button
            type="button"
            onClick={() => setEventsOpen((v) => !v)}
            aria-expanded={eventsOpen}
            className="mt-1 text-[12px] font-bold text-accent underline-offset-2 hover:underline"
          >
            {eventsOpen ? "Скрыть события" : `События исполнения (${o.eventCount})`}
          </button>
          {/* Правовой блок (E9): расчёт санкций, давность, претензии. Просмотр — по праву
              `legal:view`: все роли, КРОМЕ делопроизводителя (CR-1, ответ заказчика на В28).
              Изменения внутри панели скрыты отдельно — без права `legal:manage`.
              Запрет держит сервер: без права он отвечает 403 и на расчёт, и на печать. */}
          {canViewLegal(roles) && (
            <button
              type="button"
              onClick={() => setLegalOpen((v) => !v)}
              aria-expanded={legalOpen}
              className="mt-1 ml-3 text-[12px] font-bold text-accent underline-offset-2 hover:underline"
            >
              {legalOpen ? "Скрыть расчёт" : "Расчёт санкций и претензии"}
            </button>
          )}
        </td>
        <td className="px-4 py-3 text-[13px] text-ink">{DEBTOR_LABELS[o.debtor]}</td>
        <td className="px-4 py-3 text-[13px] text-ink">
          {o.responsible ? (
            ROLE_LABELS[o.responsible]
          ) : (
            <span className="text-muted">не назначен</span>
          )}
        </td>
        <td className="num px-4 py-3 text-ink">{formatMoney(o.amount)}</td>
        <td className="num px-4 py-3 text-ink">
          <RemainingCell o={o} />
        </td>
        <td className="px-4 py-3">
          <DueCell o={o} />
        </td>
        <td className="px-4 py-3">
          <StatusStamp o={o} />
        </td>
        {canManage && (
          <td className="px-4 py-3">
            <div className="flex flex-wrap items-center gap-2">
              {confirm ? (
                <>
                  <span className="text-[13px] text-ink">Удалить?</span>
                  <button
                    type="button"
                    disabled={busy}
                    onClick={() => void doDelete()}
                    className={btnPrimary}
                  >
                    Да
                  </button>
                  <button
                    type="button"
                    disabled={busy}
                    onClick={() => setConfirm(false)}
                    className={btnSecondary}
                  >
                    Нет
                  </button>
                </>
              ) : (
                <>
                  <button type="button" onClick={onEdit} className={btnSecondary}>
                    Изменить
                  </button>
                  <button type="button" onClick={() => setConfirm(true)} className={btnSecondary}>
                    Удалить
                  </button>
                </>
              )}
            </div>
            {error && (
              <p role="alert" className="mt-1.5 text-[12px] text-overdue-fg">
                {error}
              </p>
            )}
          </td>
        )}
      </tr>
      {eventsOpen && (
        <tr className="border-b border-border-soft bg-bg last:border-b-0">
          <td colSpan={colCount} className="px-4 py-3">
            <EventsPanel o={o} kinds={kinds} onRegistered={onChanged} />
          </td>
        </tr>
      )}
      {legalOpen && (
        <tr className="border-b border-border-soft bg-bg last:border-b-0">
          <td colSpan={colCount} className="px-4 py-3">
            <ObligationLegalPanel
              obligationId={o.id}
              roles={roles}
              onObligationChanged={() => void onChanged()}
            />
          </td>
        </tr>
      )}
    </>
  );
}

/**
 * Остаток обязательства: считает сервер из событий (Р-2). У ИСПОЛНЕННОГО обязательства числа не
 * показываем — иначе рядом со статусом «Исполнено» стояло бы «Остаток 1 000 000,00 ₽» (поставку
 * закрывают признаком полноты, суммы отгрузок при этом заполнять необязательно) и наоборот:
 * зелёный «0,00 ₽» у открытой поставки, где суммы набраны, но полнота не заявлена.
 */
function RemainingCell({ o }: { o: Obligation }) {
  if (o.doneDate !== null) return <span className="text-done-fg text-[13px]">исполнено</span>;
  if (o.remaining === null) return <span className="text-muted">—</span>;
  const covered = o.covered !== "0.00";
  return (
    <span>
      <span className="text-ink">{formatMoney(o.remaining)}</span>
      {covered && (
        <span className="mt-0.5 block text-[11px] text-muted">
          {o.money ? "внесено" : "отгружено"} {formatMoney(o.covered)}
        </span>
      )}
    </span>
  );
}

/**
 * Лента событий обязательства и форма внесения нового (E8). Виды показываются только те, что
 * подходят типу обязательства (Приложение А) И доступны роли (матрица 4.3) — серверный запрет
 * всё равно первичен (правило 5).
 */
function EventsPanel({
  o,
  kinds,
  onRegistered,
}: {
  o: Obligation;
  kinds: readonly ExecEventKind[];
  onRegistered: () => Promise<void>;
}) {
  const [events, setEvents] = useState<ExecEvent[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [formKind, setFormKind] = useState<ExecEventKind | null>(null);
  // Подтверждение автозакрытия: у денежного обязательства оно наступает «само», когда платежи
  // покрыли сумму, — об этом надо сказать вслух, иначе смена статуса выглядит необъяснимой.
  const [closedOn, setClosedOn] = useState<string | null>(null);
  // То же после сторно (E8a): снятие платежа может вернуть обязательство на контроль — это тоже
  // происходит «само», по пересчёту, и должно быть проговорено. Отдельно — сдвиг ДАТЫ исполнения
  // без переоткрытия (от неё считаются санкции).
  const [reopened, setReopened] = useState(false);
  const [shiftedFrom, setShiftedFrom] = useState<string | null>(null);

  const load = useCallback(async () => {
    const res = await fetchEvents(o.id);
    if (res.ok) {
      setEvents(res.value);
      setError(null);
      return;
    }
    setError(res.message);
  }, [o.id]);

  useEffect(() => {
    void load();
  }, [load]);

  return (
    <div>
      <div className="flex flex-wrap items-center gap-2">
        <h3 className="text-[13px] font-bold text-ink">События исполнения</h3>
        {kinds.map((k) => (
          <button
            key={k}
            type="button"
            onClick={() => setFormKind(formKind === k ? null : k)}
            className={btnSecondary}
          >
            {formKind === k ? "Отмена" : `Внести: ${EVENT_KIND_LABELS[k]}`}
          </button>
        ))}
      </div>

      {kinds.length === 0 && (
        <p className="mt-1 text-[12px] text-muted">
          События по этому обязательству вносит другая роль (Приложение А, матрица 4.3).
        </p>
      )}

      {formKind && (
        <div className="mt-3">
          <EventForm
            o={o}
            kind={formKind}
            onSaved={async (result) => {
              setFormKind(null);
              setReopened(false);
              setShiftedFrom(null);
              setClosedOn(result.closed ? result.obligation.doneDate : null);
              await load();
              await onRegistered();
            }}
            onCancel={() => setFormKind(null)}
          />
        </div>
      )}

      {closedOn && (
        <p role="status" className="mt-2 text-[13px] text-done-fg">
          Обязательство исполнено — закрыто датой {formatDate(closedOn)}.
        </p>
      )}

      {reopened && (
        <p role="status" className="mt-2 text-[13px] text-control-fg">
          Событие сторнировано — обязательство снова на контроле.
        </p>
      )}

      {/* Дата исполнения — база расчёта неустойки и процентов ст. 395 (E9). Сторно может сдвинуть
          её, не переоткрывая обязательство: молча менять такую дату нельзя. */}
      {shiftedFrom && (
        <p role="status" className="mt-2 text-[13px] text-control-fg">
          Событие сторнировано — дата исполнения изменилась с {formatDate(shiftedFrom)}.
        </p>
      )}

      {error && (
        <p role="alert" className="mt-2 text-[12px] text-overdue-fg">
          {error}
        </p>
      )}

      <div className="mt-3">
        {events === null ? (
          <p className="text-[12px] text-muted" role="status">
            Загрузка событий…
          </p>
        ) : events.length === 0 ? (
          <p className="text-[12px] text-muted">Событий пока нет.</p>
        ) : (
          <ul className="space-y-1.5">
            {events.map((e) => (
              <EventRow
                key={e.id}
                e={e}
                onReversed={async (result) => {
                  setClosedOn(null);
                  setReopened(result.reopened);
                  setShiftedFrom(result.doneDateShiftedFrom);
                  await load();
                  await onRegistered();
                }}
              />
            ))}
          </ul>
        )}
      </div>
      <p className="mt-2 text-[11px] text-muted">
        Событие нельзя изменить или удалить: ошибочная запись исправляется сторнированием, и обе
        записи остаются в журнале. <HelpLink topic="eventReversalOnly" />
      </p>
    </div>
  );
}

/**
 * Строка ленты. Сторнированное событие показывается зачёркнутым со штампом — запись не исчезает
 * (события неизменяемы), но перестаёт влиять на остаток и статус. Сама запись сторно выводится
 * отдельной строкой с причиной.
 */
function EventRow({ e, onReversed }: { e: ExecEvent; onReversed: ReverseHandler }) {
  const [formOpen, setFormOpen] = useState(false);
  const isReversal = e.reversesEventId !== null;
  const isReversed = e.reversedByEventId !== null;

  if (isReversal) {
    return (
      <li className="text-[13px] text-muted">
        <span className="num">{formatDate(e.date)}</span>
        {" — сторнирование: "}
        {EVENT_KIND_LABELS[e.kind]}
        {/* Нейтральный «спящий» токен, не тревожный: сторно — бухгалтерская пометка, а
            overdue-* закреплён за «Просрочено»/«Эскалация» (docs/07 §1, правило 9). */}
        <span className="stamp text-waiting-fg bg-waiting-bg ml-1.5">сторно</span>
        <span className="block text-[11px] text-muted">
          причина: {e.reason}; автор: {e.authorName}
        </span>
      </li>
    );
  }

  return (
    <li className="text-[13px] text-ink">
      <span className={isReversed ? "text-muted line-through" : undefined}>
        <span className="num">{formatDate(e.date)}</span>
        {" — "}
        {EVENT_KIND_LABELS[e.kind]}
        {e.amount && <span className="num"> на {formatMoney(e.amount)}</span>}
        {e.isFull === true && !isReversed && (
          <span className="stamp text-done-fg bg-done-bg ml-1.5">исполнено полностью</span>
        )}
      </span>
      {isReversed && (
        <span className="stamp text-waiting-fg bg-waiting-bg ml-1.5">сторнировано</span>
      )}
      {e.canReverse && !formOpen && (
        <button
          type="button"
          onClick={() => setFormOpen(true)}
          className="ml-2 text-[12px] font-bold text-accent underline"
        >
          Сторнировать
        </button>
      )}
      <span className="block text-[11px] text-muted">
        {e.docRef ? `документ: ${e.docRef}; ` : ""}
        автор: {e.authorName}
        {e.note ? `; ${e.note}` : ""}
      </span>
      {formOpen && (
        <ReverseForm
          e={e}
          onCancel={() => setFormOpen(false)}
          onDone={async (result) => {
            setFormOpen(false);
            await onReversed(result);
          }}
        />
      )}
    </li>
  );
}

type ReverseHandler = (result: EventReversed) => Promise<void>;

/**
 * Форма сторнирования: одна обязательная причина. Она уходит в журнал аудита (E8a) — поэтому и
 * подпись говорит об этом прямо, чтобы туда не писали «ошибка».
 */
function ReverseForm({
  e,
  onDone,
  onCancel,
}: {
  e: ExecEvent;
  onDone: ReverseHandler;
  onCancel: () => void;
}) {
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = async (ev: FormEvent) => {
    ev.preventDefault();
    setBusy(true);
    setError(null);
    const res = await reverseEvent(e.kind, e.id, reason.trim());
    setBusy(false);
    if (res.ok) {
      await onDone(res.value);
      return;
    }
    setError(res.message);
  };

  return (
    <form
      onSubmit={(ev) => void submit(ev)}
      aria-label={`Сторнирование: ${EVENT_KIND_LABELS[e.kind]} от ${formatDate(e.date)}`}
      className="mt-2 rounded-[var(--radius-l)] border border-border bg-surface p-3"
    >
      <label className={labelCls} htmlFor={`rev-reason-${e.id}`}>
        Причина сторнирования (останется в журнале аудита)
      </label>
      <input
        id={`rev-reason-${e.id}`}
        type="text"
        required
        minLength={3}
        maxLength={1000}
        value={reason}
        onChange={(ev) => setReason(ev.target.value)}
        placeholder="Например: платёж отнесён не на тот договор"
        className={inputCls}
      />
      <p className="mt-1 text-[11px] text-muted">
        Событие останется в журнале, но перестанет влиять на остаток и статус обязательства.
      </p>
      {error && (
        <p role="alert" className="mt-2 text-[12px] text-overdue-fg">
          {error}
        </p>
      )}
      <div className="mt-2 flex gap-2">
        <button type="submit" disabled={busy} className={btnPrimary}>
          {busy ? "Сторнирование…" : "Сторнировать"}
        </button>
        <button type="button" onClick={onCancel} className={btnSecondary}>
          Отмена
        </button>
      </div>
    </form>
  );
}

/**
 * Форма события. Состав полей — реквизиты ТЗ 4.2.1 (дата, сумма, № подтверждающего документа,
 * примечание). Признак «исполнено полностью» показывается ТОЛЬКО там, где закрытие заявляет автор:
 * у денежного обязательства с суммой обязательство закрывает полное покрытие платежами (сервер).
 */
function EventForm({
  o,
  kind,
  onSaved,
  onCancel,
}: {
  o: Obligation;
  kind: ExecEventKind;
  onSaved: (result: EventRegistered) => Promise<void>;
  onCancel: () => void;
}) {
  // Автозакрытие покрытием — только для ПЛАТЕЖА по денежному обязательству с суммой. Передача
  // банковской гарантии по «Обеспечению» суммы не вносит: там полноту заявляет автор (см. сервис).
  const closedByCoverage = kind === "payment" && o.money && o.amount !== null;
  const [date, setDate] = useState("");
  const [amount, setAmount] = useState("");
  const [docRef, setDocRef] = useState("");
  const [note, setNote] = useState("");
  // По умолчанию ВЫКЛЮЧЕН: снять обязательство с контроля — необратимое до сторно (E8a) действие,
  // и оно не должно быть «действием по умолчанию» при невнимательном клике.
  const [isFull, setIsFull] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    const res = await registerEvent(kind, {
      obligationId: o.id,
      date,
      ...(amount.trim() === "" ? {} : { amount: amount.trim() }),
      ...(docRef.trim() === "" ? {} : { docRef: docRef.trim() }),
      ...(note.trim() === "" ? {} : { note: note.trim() }),
      ...(closedByCoverage ? {} : { isFull }),
    });
    setBusy(false);
    if (res.ok) {
      await onSaved(res.value);
      return;
    }
    setError(res.message);
  };

  return (
    <form
      onSubmit={(e) => void submit(e)}
      aria-label={`Событие: ${EVENT_KIND_LABELS[kind]}`}
      className="rounded-[var(--radius-l)] border border-border bg-surface p-3"
    >
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <div>
          <label className={labelCls} htmlFor={`ev-date-${o.id}`}>
            Дата события
          </label>
          <input
            id={`ev-date-${o.id}`}
            type="date"
            required
            value={date}
            onChange={(e) => setDate(e.target.value)}
            className={inputCls}
          />
        </div>
        <div>
          <label className={labelCls} htmlFor={`ev-amount-${o.id}`}>
            Сумма, ₽{kind === "payment" ? "" : " (если применимо)"}
          </label>
          <input
            id={`ev-amount-${o.id}`}
            inputMode="decimal"
            required={kind === "payment"}
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            className={inputCls}
          />
        </div>
        <div>
          <label className={labelCls} htmlFor={`ev-doc-${o.id}`}>
            № подтверждающего документа{kind === "payment" ? "" : " (если есть)"}
          </label>
          <input
            id={`ev-doc-${o.id}`}
            // У платежа реквизит обязателен (ТЗ 4.2.1) — он же отличает два платежа одной датой
            // на одну сумму и не даёт повторной отправке формы задвоить оплату.
            required={kind === "payment"}
            value={docRef}
            onChange={(e) => setDocRef(e.target.value)}
            placeholder={kind === "payment" ? "п/п № 451" : "УПД № 12"}
            className={inputCls}
          />
        </div>
        <div>
          <label className={labelCls} htmlFor={`ev-note-${o.id}`}>
            Примечание
          </label>
          <input
            id={`ev-note-${o.id}`}
            value={note}
            onChange={(e) => setNote(e.target.value)}
            className={inputCls}
          />
        </div>
      </div>

      {closedByCoverage ? (
        <p className="mt-2 text-[12px] text-muted">
          Обязательство закроется автоматически, когда платежи покроют сумму целиком — датой
          последнего платежа.
        </p>
      ) : (
        <label className="mt-2 flex items-center gap-2 text-[13px] text-ink">
          <input
            type="checkbox"
            checked={isFull}
            onChange={(e) => setIsFull(e.target.checked)}
            className="size-4"
          />
          Обязательство исполнено полностью
        </label>
      )}

      {error && (
        <p role="alert" className="mt-2 text-[13px] text-overdue-fg">
          {error}
        </p>
      )}

      <div className="mt-3 flex gap-2">
        <button type="submit" disabled={busy} className={btnPrimary}>
          Зарегистрировать
        </button>
        <button type="button" disabled={busy} onClick={onCancel} className={btnSecondary}>
          Отмена
        </button>
      </div>
    </form>
  );
}

const TYPE_OPTIONS = Object.keys(OBLIGATION_TYPE_LABELS) as ObligationType[];

function ObligationForm({
  contractId,
  specifications,
  obligations,
  initial,
  onSaved,
  onCancel,
}: {
  contractId: string;
  specifications: Specification[];
  obligations: Obligation[];
  initial?: Obligation;
  onSaved: () => void;
  onCancel: () => void;
}) {
  const [type, setType] = useState<ObligationType>(initial?.type ?? "payment_post");
  const [debtor, setDebtor] = useState<ObligationDebtor>(initial?.debtor ?? "they");
  // Ответственная роль. Пустая строка — «по умолчанию из типа» (Прил. А); выбранная роль —
  // переопределение, доступное для ЛЮБОГО типа (E10, ТЗ 4.2.4: уведомления адресуются
  // назначенному ответственному). У типа «Иное» умолчания нет — роль обязательна.
  const [responsible, setResponsible] = useState<Role | "">(initial?.respRole ?? "");
  const [descr, setDescr] = useState(initial?.descr ?? "");
  const [amount, setAmount] = useState(initial?.amount ?? "");
  const [specificationId, setSpecificationId] = useState(initial?.specificationId ?? "");
  const [ruleKind, setRuleKind] = useState<ObligationRuleKind>(initial?.ruleKind ?? "fixed");
  const [ruleDate, setRuleDate] = useState(initial?.ruleDate ?? "");
  const [ruleBase, setRuleBase] = useState<ObligationRuleBase>(initial?.ruleBase ?? "contract");
  const [ruleDays, setRuleDays] = useState(
    initial?.ruleDays == null ? "" : String(initial.ruleDays),
  );
  const [dayType, setDayType] = useState<ObligationDayType>(initial?.dayType ?? "calendar");
  const [triggerId, setTriggerId] = useState(initial?.triggerObligationId ?? "");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Обязательства-триггеры — прочие обязательства этого договора (не сам редактируемый).
  const triggerOptions = obligations.filter((o) => o.id !== initial?.id);

  // Какая роль подставится, если не переопределять (Приложение А). У «Иного» умолчания нет.
  const defaultResp = OBLIGATION_DEFAULT_RESP_ROLE[type];
  const defaultRespLabel = defaultResp ? ROLE_LABELS[defaultResp] : "не определена";
  // Назначить ответственным можно любую роль (уведомления, ТЗ 4.2.4), но неденежные события
  // вносят только коммерческий отдел, юрист и админ (4.3). Если выбрана другая роль, обязательство
  // сможет закрыть лишь юрист/админ — предупреждаем сразу, а не после отказа сервера.
  const cannotCloseItself = responsible !== "" && !canRegisterClosingEvent(type, responsible);

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    // Собираем правило по виду — сервер валидирует полноту и нормализует лишние поля.
    const rule: ObligationInput =
      ruleKind === "fixed"
        ? { ruleKind, ruleDate }
        : ruleKind === "relative"
          ? { ruleKind, ruleBase, ruleDays, dayType }
          : { ruleKind, triggerObligationId: triggerId, ruleDays, dayType };
    const input: ObligationInput = {
      type,
      debtor,
      descr: descr.trim(),
      amount: amount.trim(),
      specificationId: specificationId === "" ? null : specificationId,
      // `null` — осмысленное значение: «вернуть роль по умолчанию из типа». Отправляем всегда,
      // чтобы снятие переопределения доходило до сервера так же явно, как и назначение.
      responsible: responsible === "" ? null : responsible,
      ...rule,
    };
    const res = initial
      ? await updateObligation(initial.id, input)
      : await createObligation(contractId, input);
    setBusy(false);
    if (res.ok) {
      onSaved();
      return;
    }
    setError(res.message);
  };

  return (
    <form onSubmit={onSubmit} className="rounded-[var(--radius-m)] border border-border bg-bg p-3">
      <div className="grid gap-3 sm:grid-cols-2">
        <div>
          <label htmlFor="ob-type" className={labelCls}>
            Тип обязательства
          </label>
          <select
            id="ob-type"
            value={type}
            onChange={(e) => {
              setType(e.target.value as ObligationType);
              // Роль назначалась для ПРЕЖНЕГО типа — к новому она отношения не имеет; сервер
              // при смене типа поступает так же (сброс к умолчанию нового типа).
              setResponsible("");
            }}
            className={inputCls}
          >
            {TYPE_OPTIONS.map((t) => (
              <option key={t} value={t}>
                {OBLIGATION_TYPE_LABELS[t]}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label htmlFor="ob-debtor" className={labelCls}>
            Должник
          </label>
          <select
            id="ob-debtor"
            value={debtor}
            onChange={(e) => setDebtor(e.target.value as ObligationDebtor)}
            className={inputCls}
          >
            <option value="they">{DEBTOR_LABELS.they}</option>
            <option value="we">{DEBTOR_LABELS.we}</option>
          </select>
        </div>
        <div className="sm:col-span-2">
          <label htmlFor="ob-resp" className={labelCls}>
            Ответственная роль — ей адресуются уведомления
          </label>
          <select
            id="ob-resp"
            value={responsible}
            onChange={(e) => setResponsible(e.target.value as Role | "")}
            className={inputCls}
            required={type === "other"}
          >
            {/* У «Иного» умолчания нет (Прил. А) — вместо него пустой пункт-заглушка, который
                не даёт отправить форму, пока роль не выбрана. */}
            {type === "other" ? (
              <option value="">— выберите роль —</option>
            ) : (
              <option value="">По умолчанию из типа ({defaultRespLabel})</option>
            )}
            {ROLES.map((r) => (
              <option key={r} value={r}>
                {ROLE_LABELS[r]}
              </option>
            ))}
          </select>
          <p className="mt-1 text-[12px] text-muted">
            {type === "other"
              ? "Для типа «Иное» ответственную роль назначают вручную — по умолчанию она не определена."
              : `Если не выбирать, ответственным будет ${defaultRespLabel} — как задано типом обязательства (Приложение А ТЗ).`}
          </p>
          {cannotCloseItself && (
            <p className="mt-1 text-[12px] text-overdue-fg">
              Уведомления этой роли приходить будут, но отметить обязательство исполненным она не
              сможет — события отгрузки, приёмки и передачи документов вносит коммерческий отдел
              (матрица 4.3). Закрыть такое обязательство смогут юрист или администратор.
            </p>
          )}
        </div>
        <div className="sm:col-span-2">
          <label htmlFor="ob-descr" className={labelCls}>
            Описание обязательства
          </label>
          <input
            id="ob-descr"
            value={descr}
            onChange={(e) => setDescr(e.target.value)}
            className={inputCls}
            required
          />
        </div>
        <div>
          <label htmlFor="ob-amount" className={labelCls}>
            Сумма, ₽
          </label>
          <input
            id="ob-amount"
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            inputMode="decimal"
            placeholder="—"
            className={`num ${inputCls}`}
          />
        </div>
        <div>
          <label htmlFor="ob-spec" className={labelCls}>
            Спецификация (необязательно)
          </label>
          <select
            id="ob-spec"
            value={specificationId}
            onChange={(e) => setSpecificationId(e.target.value)}
            className={inputCls}
          >
            <option value="">— договор в целом —</option>
            {specifications.map((s) => (
              <option key={s.id} value={s.id}>
                № {s.number} от {formatDate(s.date)}
              </option>
            ))}
          </select>
        </div>
      </div>

      <fieldset className="mt-3 rounded-[var(--radius-m)] border border-border-soft p-3">
        <legend className="px-1 text-[13px] text-muted">Правило срока</legend>
        <div className="grid gap-3 sm:grid-cols-2">
          <div className="sm:col-span-2">
            <label htmlFor="ob-rulekind" className={labelCls}>
              Вид правила
            </label>
            <select
              id="ob-rulekind"
              value={ruleKind}
              onChange={(e) => setRuleKind(e.target.value as ObligationRuleKind)}
              className={inputCls}
            >
              <option value="fixed">До конкретной даты</option>
              <option value="relative">N дней от даты договора/спецификации</option>
              <option value="event">N дней от исполнения другого обязательства</option>
            </select>
          </div>

          {ruleKind === "fixed" && (
            <div>
              <label htmlFor="ob-ruledate" className={labelCls}>
                Дата срока
              </label>
              <input
                id="ob-ruledate"
                type="date"
                value={ruleDate}
                onChange={(e) => setRuleDate(e.target.value)}
                className={`num ${inputCls}`}
                required
              />
            </div>
          )}

          {ruleKind === "relative" && (
            <div>
              <label htmlFor="ob-rulebase" className={labelCls}>
                Отсчёт от даты
              </label>
              <select
                id="ob-rulebase"
                value={ruleBase}
                onChange={(e) => setRuleBase(e.target.value as ObligationRuleBase)}
                className={inputCls}
              >
                <option value="contract">{RULE_BASE_LABELS.contract}</option>
                <option value="spec">{RULE_BASE_LABELS.spec}</option>
              </select>
            </div>
          )}

          {ruleKind === "event" && (
            <div>
              <label htmlFor="ob-trigger" className={labelCls}>
                Обязательство-триггер
              </label>
              <select
                id="ob-trigger"
                value={triggerId}
                onChange={(e) => setTriggerId(e.target.value)}
                className={inputCls}
                required
              >
                <option value="">— выберите обязательство —</option>
                {triggerOptions.map((t) => (
                  <option key={t.id} value={t.id}>
                    {t.typeLabel}: {t.descr.slice(0, 40)}
                  </option>
                ))}
              </select>
            </div>
          )}

          {ruleKind !== "fixed" && (
            <>
              <div>
                <label htmlFor="ob-ruledays" className={labelCls}>
                  Число дней
                </label>
                <input
                  id="ob-ruledays"
                  type="number"
                  min={0}
                  value={ruleDays}
                  onChange={(e) => setRuleDays(e.target.value)}
                  className={`num ${inputCls}`}
                  required
                />
              </div>
              <div>
                <label htmlFor="ob-daytype" className={labelCls}>
                  Тип дней
                </label>
                <select
                  id="ob-daytype"
                  value={dayType}
                  onChange={(e) => setDayType(e.target.value as ObligationDayType)}
                  className={inputCls}
                >
                  <option value="calendar">{DAY_TYPE_LABELS.calendar}</option>
                  <option value="work">{DAY_TYPE_LABELS.work}</option>
                  <option value="bank">{DAY_TYPE_LABELS.bank}</option>
                </select>
              </div>
            </>
          )}
        </div>
        {ruleKind === "relative" && ruleBase === "spec" && specificationId === "" && (
          <p className="mt-2 text-[12px] text-soon-fg">
            Для отсчёта от спецификации выберите спецификацию выше.
          </p>
        )}
      </fieldset>

      {error && (
        <p role="alert" className="mt-3 text-[13px] text-overdue-fg">
          {error}
        </p>
      )}
      <div className="mt-3 flex items-center gap-2">
        <button type="submit" disabled={busy} className={btnPrimary}>
          {busy ? "Сохранение…" : initial ? "Сохранить" : "Добавить"}
        </button>
        <button type="button" disabled={busy} onClick={onCancel} className={btnSecondary}>
          Отмена
        </button>
      </div>
    </form>
  );
}
